/**
 * TypeScript types for the Global Chat frontend
 *
 * These types match the API response shapes from:
 * - POST /api/chat/global (start conversation)
 * - GET /api/chat/global (get conversations/conversation)
 * - POST /api/chat/global/respond (send message)
 * - DELETE /api/chat/global (end conversation)
 */

/**
 * Message role for global chat conversation
 */
export type GlobalChatMessageRole = 'assistant' | 'user';

/**
 * Single message in the global chat conversation
 * Matches the message shape returned by GET /api/chat/global
 */
export interface GlobalChatMessage {
  id: number;
  role: GlobalChatMessageRole;
  content: string;
  createdAt: string;
}

/**
 * Response from POST /api/chat/global (start conversation)
 */
export interface StartGlobalChatResponse {
  success: boolean;
  conversationId?: number;
  conversationUuid?: string;
  message?: string;
  tokensUsed?: number;
  error?: string;
}

/**
 * Single conversation summary returned in the list
 */
export interface GlobalChatConversationSummary {
  id: number;
  uuid: string;
  status: string;
  messageCount: number;
  createdAt: string;
  updatedAt: string;
}

/**
 * Full conversation with messages
 */
export interface GlobalChatConversationWithMessages {
  id: number;
  uuid: string;
  status: string;
  createdAt: string;
  messages: GlobalChatMessage[];
}

/**
 * Response from GET /api/chat/global (get conversations)
 */
export interface GetGlobalChatResponse {
  success: boolean;
  conversation?: GlobalChatConversationWithMessages;
  conversations?: GlobalChatConversationSummary[];
  error?: string;
}

/**
 * Response from POST /api/chat/global/respond
 */
export interface GlobalChatRespondResponse {
  success: boolean;
  message?: string;
  tokensUsed?: number;
  conversationId?: number;
  conversationUuid?: string;
  error?: string;
}

/**
 * Response from DELETE /api/chat/global
 */
export interface EndGlobalChatResponse {
  success: boolean;
  error?: string;
}

/**
 * Complete global chat state for the frontend
 * Aggregates conversation info and messages for UI rendering
 */
export interface GlobalChatState {
  /** Whether a chat is currently open/active */
  isOpen: boolean;
  /** Whether there is an active conversation */
  hasActiveConversation: boolean;
  /** The conversation ID */
  conversationId: number | null;
  /** The conversation UUID for reference */
  conversationUuid: string | null;
  /** All messages in the conversation */
  messages: GlobalChatMessage[];
  /** Previous conversation summaries */
  previousConversations: GlobalChatConversationSummary[];
}

/**
 * Request body for starting a new global chat
 */
export interface StartGlobalChatRequest {
  initialMessage?: string;
}

/**
 * Request body for sending a message
 */
export interface GlobalChatRespondRequest {
  conversationId: number;
  message: string;
}

/**
 * Request body for ending a conversation
 */
export interface EndGlobalChatRequest {
  conversationId: number;
}

/**
 * Hook return type for useGlobalChat
 */
export interface UseGlobalChatReturn {
  /** Current chat state */
  state: GlobalChatState;
  /** Whether an API request is in progress */
  isLoading: boolean;
  /** Whether a message is being sent (AI thinking) */
  isSending: boolean;
  /** Latest error message, if any */
  error: string | null;
  /** Whether the FAB should be hidden (e.g., during full-screen dialogs) */
  hideFab: boolean;
  /** Open the chat panel */
  openChat: () => void;
  /** Close the chat panel */
  closeChat: () => void;
  /** Toggle the chat panel open/closed */
  toggleChat: () => void;
  /** Start a new conversation (optionally with an initial message) */
  startConversation: (initialMessage?: string) => Promise<boolean>;
  /** Send a message to the current conversation */
  sendMessage: (message: string) => Promise<boolean>;
  /** End the current conversation */
  endConversation: () => Promise<boolean>;
  /** Load previous conversations */
  loadConversations: (limit?: number) => Promise<void>;
  /** Load a specific conversation by ID */
  loadConversation: (conversationId: number) => Promise<boolean>;
  /** Clear any error state */
  clearError: () => void;
  /** Reset the chat to initial state */
  resetChat: () => void;
  /** Set whether the FAB should be hidden */
  setHideFab: (hide: boolean) => void;
}

/**
 * Default/initial global chat state
 */
export const INITIAL_GLOBAL_CHAT_STATE: GlobalChatState = {
  isOpen: false,
  hasActiveConversation: false,
  conversationId: null,
  conversationUuid: null,
  messages: [],
  previousConversations: [],
};
